import React, { useEffect, type SyntheticEvent, type ReactNode } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { SpotlightTarget, SpotlightTransition } from '@atlaskit/onboarding';
import { token, useThemeObserver } from '@atlaskit/tokens';
import { useProject } from '@atlassian/jira-business-entity-project-hook/src/index.tsx';
import EditTicketImageDark from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/software/assets/edit-ticket-with-background-dark.svg';
import EditTicketImageLight from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/software/assets/edit-ticket-with-background-light.svg';
import HierarchyImageDark from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/software/assets/hierarchy-tasks-with-background-dark.svg';
import HierarchyImageLight from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/software/assets/hierarchy-tasks-with-background-light.svg';
import { useIntl, type MessageDescriptor } from '@atlassian/jira-intl';
import {
	useListViewOnboarding,
	type SpotlightId,
	CREATE_CHILD_SPOTLIGHT_ID,
	CREATE_FIELD_SPOTLIGHT_ID,
	useOnboardingGetStartedTourActions,
} from '@atlassian/jira-onboarding-get-started-tour/src/controllers/list-view-tour/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { JiraSpotlight } from '@atlassian/jira-spotlight/src/ui/jira-spotlight.tsx';
import { isTailoredOnboardingEnabled } from '@atlassian/jira-tailored-onboarding/src/controllers/index.tsx';
import {
	SHOULD_SHOW_LIST_VIEW_TYPE_CELL_SPOTLIGHT,
	SHOULD_SHOW_LIST_VIEW_FIELDS_DROPDOWN_SPOTLIGHT,
} from '@atlassian/jira-onboarding-core/src/constants.tsx';
import { sessionStorageProvider } from '@atlassian/jira-onboarding-core/src/utils/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { messages } from './messages.tsx';

type SpotlightMessages = {
	heading: MessageDescriptor;
	body: MessageDescriptor;
	dismiss: MessageDescriptor;
};

const spotlightMessages: { [key in SpotlightId]: SpotlightMessages } = {
	[CREATE_CHILD_SPOTLIGHT_ID]: {
		heading: messages.createChildHeading,
		body: messages.createChildBody,
		dismiss: messages.dismiss,
	},
	[CREATE_FIELD_SPOTLIGHT_ID]: {
		heading: messages.createFieldHeading,
		body: messages.createFieldBody,
		dismiss: messages.dismiss,
	},
};

const spotlightImageUrls: { [key in SpotlightId]: { light: string; dark: string } } = {
	[CREATE_CHILD_SPOTLIGHT_ID]: {
		light: HierarchyImageLight,
		dark: HierarchyImageDark,
	},
	[CREATE_FIELD_SPOTLIGHT_ID]: {
		light: EditTicketImageLight,
		dark: EditTicketImageDark,
	},
};

const useShouldShowListViewTour = ({ isSpotlightVisible }: { isSpotlightVisible: boolean }) => {
	const projectData = useProject();
	const projectId = projectData.id;
	const { startTour } = useOnboardingGetStartedTourActions();

	useEffect(() => {
		if (!isSpotlightVisible && projectId && shouldWrapTypeCellOnboardingSpotlight()) {
			startTour(String(projectId));
		}
	}, [isSpotlightVisible, projectId, startTour]);
};

export const OnboardingSpotlightWrapper = ({
	renderTrigger,
	spotlightId,
	onClickHandler,
}: {
	spotlightId: SpotlightId;
	renderTrigger: (isSpotlightVisible: boolean) => ReactNode;
	onClickHandler?: () => void;
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const projectData = useProject();
	const [isSpotlightVisible, actions] = useListViewOnboarding({
		projectId: String(projectData.id),
		id: spotlightId,
	});
	const { colorMode } = useThemeObserver();

	useEffect(() => {
		if (isSpotlightVisible) {
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'viewed',
					actionSubject: 'spotlight',
				}),
				spotlightId,
			);
		}
	}, [createAnalyticsEvent, isSpotlightVisible, spotlightId]);

	useShouldShowListViewTour({ isSpotlightVisible });

	const onClick = (_: SyntheticEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
		actions.dismissSpotlight(spotlightId);
		fireUIAnalytics(analyticsEvent, `${spotlightId}Dismiss`);
		onClickHandler && onClickHandler();
	};

	const { heading, body, dismiss } = spotlightMessages[spotlightId];
	const { dark, light } = spotlightImageUrls[spotlightId];
	const imageUrl = colorMode === 'dark' ? dark : light;

	if (isSpotlightVisible) {
		return (
			<>
				<SpotlightTarget name={spotlightId}>{renderTrigger(isSpotlightVisible)}</SpotlightTarget>
				<SpotlightTransition>
					<JiraSpotlight
						image={imageUrl}
						actions={[
							{
								onClick,
								text: formatMessage(dismiss),
							},
						]}
						heading={formatMessage(heading)}
						target={spotlightId}
						key={spotlightId}
						targetRadius={3}
						targetBgColor={token('elevation.surface')}
						messageId={spotlightId}
						messageType="transactional"
						dialogWidth={275}
					>
						{formatMessage(body)}
					</JiraSpotlight>
				</SpotlightTransition>
			</>
		);
	}

	return renderTrigger(isSpotlightVisible);
};

export const HierarchySpotlightWrapper = ({
	renderTrigger,
}: {
	renderTrigger: (isSpotlightVisible: boolean) => ReactNode;
}) => {
	return (
		<OnboardingSpotlightWrapper
			spotlightId={CREATE_CHILD_SPOTLIGHT_ID}
			renderTrigger={renderTrigger}
			onClickHandler={() => {
				sessionStorageProvider.remove(SHOULD_SHOW_LIST_VIEW_TYPE_CELL_SPOTLIGHT);
			}}
		/>
	);
};

export const FieldsSpotlightWrapper = ({
	renderTrigger,
}: {
	renderTrigger: (isSpotlightVisible: boolean) => ReactNode;
}) => {
	return (
		<OnboardingSpotlightWrapper
			spotlightId={CREATE_FIELD_SPOTLIGHT_ID}
			renderTrigger={renderTrigger}
			onClickHandler={() => {
				sessionStorageProvider.remove(SHOULD_SHOW_LIST_VIEW_FIELDS_DROPDOWN_SPOTLIGHT);
			}}
		/>
	);
};

export const shouldWrapTypeCellOnboardingSpotlight = () => {
	return (
		(isTailoredOnboardingEnabled() || fg('tailored_onboarding_m2p5_gate')) &&
		sessionStorageProvider.get(SHOULD_SHOW_LIST_VIEW_TYPE_CELL_SPOTLIGHT) === true
	);
};

export const shouldWrapFieldsDropdownOnboardingSpotlight = () => {
	return (
		(isTailoredOnboardingEnabled() || fg('tailored_onboarding_m2p5_gate')) &&
		sessionStorageProvider.get(SHOULD_SHOW_LIST_VIEW_FIELDS_DROPDOWN_SPOTLIGHT) === true
	);
};
